import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { authenticatedFetch } from '../hooks/useAuth';
import { useI18n } from '../i18n/index.jsx';

export default function Settings() {
  const { t, locale, setLocale } = useI18n();
  const [profile, setProfile] = useState({ displayName: '', locale: 'fr-FR', theme: 'system' });
  const [token, setToken] = useState(localStorage.getItem('mcp_token') || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showSoon, setShowSoon] = useState(false);
  const [extLinking, setExtLinking] = useState(false);
  const [extLinked, setExtLinked] = useState(false);
  const accessToken = typeof window !== 'undefined' ? (localStorage.getItem('access_token') || '') : '';

  // Placeholder: could fetch existing profile from API later
  useEffect(() => {
    setProfile((p) => ({ ...p }));
  }, []);

  const saveProfile = async () => {
    try {
      setSaving(true);
      setMessage('');
      // Optional: POST to /api/profile/update if exists
      // await authenticatedFetch('/api/profile/update', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(profile) });
      setMessage('Paramètres enregistrés.');
    } catch (e) {
      setMessage(`Erreur: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  function generateLocalToken(bytes = 32) {
    try {
      const arr = new Uint8Array(bytes);
      (crypto?.getRandomValues ? crypto.getRandomValues(arr) : arr.fill(Math.floor(Math.random() * 256)));
      return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
    } catch {
      // Fallback simple
      return Array.from({ length: bytes }, () => Math.floor(Math.random() * 256).toString(16).padStart(2, '0')).join('');
    }
  }

  const createMcpToken = () => {
    // Coming soon placeholder: no token creation yet
    setShowSoon(true);
    setMessage('Création de jeton MCP bientôt disponible.');
  };

  const deleteMcpToken = () => {
    localStorage.removeItem('mcp_token');
    setToken('');
    setMessage('Jeton MCP supprimé.');
  };

  const copyMcpToken = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setMessage('Jeton copié dans le presse‑papiers.');
    } catch {
      setMessage('Impossible de copier le jeton.');
    }
  };

  async function linkBrowserExtension() {
    try {
      setExtLinking(true);
      setMessage('');
      const extId = localStorage.getItem('ext_id') || '';
      const accessToken = (localStorage.getItem('access_token') || '').trim();
      if (!accessToken) throw new Error('Aucun token d’accès trouvé. Connectez-vous.');
      // If extension ID is known, send external message; otherwise, fallback to broadcast in-window (extension may listen via content script later)
      const payload = { type: 'link-account', token: accessToken };
      const sendExternal = () => new Promise((resolve, reject) => {
        if (!extId) return reject(new Error('Extension ID inconnue. Ouvrez les options et copiez l’ID.'));
        try {
          chrome.runtime.sendMessage(extId, payload, (res) => {
            const err = chrome.runtime.lastError?.message;
            if (err) return reject(new Error(err));
            if (!res?.ok) return reject(new Error(res?.error || 'Échec de liaison.'));
            resolve(res);
          });
        } catch (e) { reject(e); }
      });
      // Try external messaging if chrome.runtime is accessible
      if (typeof chrome !== 'undefined' && chrome?.runtime?.sendMessage) {
        await sendExternal();
      } else {
        // As a fallback, instruct user to paste the token in options
        throw new Error('Veuillez ouvrir les options de l’extension et coller le token pour lier.');
      }
      setExtLinked(true);
      setMessage('Extension liée à votre compte.');
    } catch (e) {
      setExtLinked(false);
      setMessage(`Liaison extension: ${e.message}`);
    } finally {
      setExtLinking(false);
    }
  }

  return (
    <DashboardLayout>
      <div className="flex-1 overflow-auto">
        <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/25 blur-3xl hidden md:block" />
        <div className="pointer-events-none absolute bottom-[-90px] right-[-90px] h-96 w-96 rounded-full bg-sky-500/20 blur-3xl hidden md:block" />

        <div className="relative z-10 py-16 px-5 sm:px-6">
          <div className="mx-auto max-w-4xl space-y-8">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold">{t('settings.title')}</h1>
            </div>

            {message && (
              <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-sm text-slate-200">
                {message}
              </div>
            )}

            {/* Langue seulement (remplacé Profil) */}
            <section className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-2xl">
              <h2 className="text-lg font-semibold mb-4">{t('settings.language')}</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm text-slate-300">Langue de l'interface</label>
                  <select
                    value={profile.locale}
                    onChange={(e)=>{
                      setProfile({ ...profile, locale: e.target.value });
                      setLocale(e.target.value);
                    }}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-slate-950/60 px-3 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="fr-FR">Français</option>
                    <option value="en-US">English</option>
                  </select>
                </div>
              </div>
            </section>

            {showSoon && (
              <div className="fixed inset-0 z-[200] flex items-center justify-center">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={()=>setShowSoon(false)} />
                <div className="relative z-10 w-[92vw] max-w-md rounded-2xl border border-white/10 bg-slate-900/80 p-5 text-slate-200 shadow-2xl">
                  <h3 className="text-lg font-semibold">{t('mcp.soonTitle')}</h3>
                  <p className="mt-2 text-sm text-slate-400">{t('mcp.soonText')}</p>
                  <div className="mt-5 flex items-center justify-end">
                    <button onClick={()=>setShowSoon(false)} className="rounded-lg bg-white/5 px-4 py-2 text-sm text-slate-200 hover:bg-white/10">OK</button>
                  </div>
                </div>
              </div>
            )}

            {/* Jeton MCP */}
            <section className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-2xl">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">{t('mcp.title')}</h2>
                <span className="text-xs rounded-full bg-amber-500/20 text-amber-300 px-2 py-1 border border-amber-500/30">{t('mcp.badge')}</span>
              </div>
              <p className="text-sm text-slate-400 mb-3">{t('mcp.description')}</p>
              <div className="space-y-3">
                {token ? (
                  <div className="rounded-lg border border-white/10 bg-slate-950/60 p-3">
                    <div className="flex items-center justify-between">
                      <code className="text-xs text-slate-300 break-all">{token.slice(0,8)}••••••••{token.slice(-6)}</code>
                      <div className="flex items-center gap-2">
                        <button onClick={copyMcpToken} className="rounded-lg bg-white/5 px-3 py-1.5 text-xs text-slate-200 hover:bg-white/10">{t('mcp.copy')}</button>
                        <button onClick={deleteMcpToken} className="rounded-lg bg-white/5 px-3 py-1.5 text-xs text-slate-200 hover:bg-white/10">{t('mcp.delete')}</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-slate-400">{t('mcp.none')}</div>
                )}
                <button onClick={createMcpToken} className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-slate-950 hover:bg-emerald-400">{t('mcp.createToken')}</button>
              </div>
            </section>

            {/* Lier l'extension navigateur */}
            <section className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-2xl">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">Lier l’extension</h2>
                {/* Badge d'état supprimé */}
              </div>
              <p className="text-sm text-slate-400 mb-3">Associez votre session au navigateur pour permettre à l’extension d’appeler l’API avec votre compte.</p>
              <div className="flex items-center gap-2">
                {/* Bouton 'Lier l’extension' supprimé */}
              </div>
              <div className="mt-3 rounded-lg border border-white/10 bg-slate-950/60 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs text-slate-400">
                    Token d’accès: {accessToken ? (<code className="text-slate-300">{accessToken.slice(0,8)}••••••{accessToken.slice(-6)}</code>) : 'Aucun (connectez-vous)'}
                  </div>
                  <button
                    onClick={async () => {
                      if (!accessToken) { setMessage('Aucun token à copier.'); return; }
                      try { await navigator.clipboard.writeText(accessToken); setMessage('Token copié.'); } catch { setMessage('Impossible de copier le token.'); }
                    }}
                    className="rounded-lg bg-white/5 px-3 py-1.5 text-xs text-slate-200 hover:bg-white/10"
                  >Copier le token</button>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">Astuce: si la liaison automatique échoue, ouvrez l’extension &gt; Options et collez le token dans la zone dédiée.</p>
            </section>

            {/* Sécurité */}
            <section className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-2xl">
              <h2 className="text-lg font-semibold mb-3">{t('security.title')}</h2>
              <ul className="list-disc pl-5 text-sm text-slate-400 space-y-1">
                <li>{t('security.tip1')}</li>
                <li>{t('security.tip2')}</li>
              </ul>
            </section>

             {/* Contact Pro */}
             <section className="mt-6 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-5 backdrop-blur-2xl">
               <h2 className="text-lg font-semibold mb-2 text-emerald-400">{t('support.title')}</h2>
               <p className="text-sm text-slate-300">{t('support.proText', { email: <a href="mailto:hiperyon@gmail.com" className="underline text-emerald-400">hiperyon@gmail.com</a> })}</p>
             </section>
            <div className="mt-4 flex justify-center">
              <a
                href="https://discord.gg/p8PFvKKqhm"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block rounded-lg bg-sky-500 px-5 py-2 text-sm font-medium text-white shadow hover:bg-sky-400 transition"
              >
                {t('support.discordButton')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
